import { Edge, Node } from '@xyflow/react';

import {
  Condition,
  ConditionalStep,
  ForLoopStep,
  MatchType,
  SpecializedAgentStep,
  Step,
  StepType,
  Trigger,
  WorkflowAgent,
} from '@frontend/types/workflow-agent';

import {
  ConditionalStepNode,
  ConditionBranchNode,
  ForLoopNode,
  isConditionalStepNode,
  isConditionNode,
  isForLoopNode,
  isSpecializedNode,
  isStepNode,
  isTriggerNode,
  SpecializedNode,
  WorkflowEdge,
  WorkflowNode,
} from '../types';

import { generateConditionNodeId } from './workflow-id-utils';

/**
 * Extract trigger data from workflow nodes.
 * isTriggerNode narrows to TriggerNode, so node.data is already typed as Trigger.
 */
function extractTriggers(workflowNodes: WorkflowNode[]): Trigger[] {
  return workflowNodes.filter(isTriggerNode).map(node => node.data);
}

/**
 * Convert React Flow graph (nodes + edges) to backend WorkflowAgent format (API type).
 */
export function convertToWorkflowAgent(
  nodes: Node[],
  edges: Edge[],
  baseWorkflowAgent: WorkflowAgent
): { agentData: WorkflowAgent; triggers: Trigger[] } {
  // Cast to typed nodes/edges - these are properly typed from React Flow
  const workflowNodes = nodes as WorkflowNode[];
  const workflowEdges = edges as WorkflowEdge[];

  // Extract triggers
  const triggers = extractTriggers(workflowNodes);

  // Process top-level nodes (not nested)
  const topLevelNodes = workflowNodes.filter(node => !node.parentId);
  const steps: Record<string, Step> = {};

  // Process each top-level node
  topLevelNodes.forEach(node => {
    const step = processStepNode(node, workflowNodes, workflowEdges);
    if (step) {
      steps[node.id] = step;
    }
  });

  // Set next_step_id relationships based on edges
  setNextStepRelationships(steps, workflowEdges, workflowNodes);

  // Find start step
  const startStepId = findStartStep(topLevelNodes, workflowEdges);

  // Create metadata with nodes, updating condition node IDs to be deterministic
  const metadataNodes = workflowNodes.map(node => {
    let nodeId = node.id;

    if (isConditionNode(node)) {
      // node.data is typed as ConditionNodeData after type guard
      const conditionData = node.data;
      const incomingEdge = workflowEdges.find(edge => edge.target === node.id);
      if (incomingEdge) {
        const parentNode = workflowNodes.find(n => n.id === incomingEdge.source);
        if (parentNode && parentNode.type === 'conditional') {
          if (
            conditionData.matching_condition !== undefined &&
            conditionData.match_type !== undefined
          ) {
            nodeId = generateConditionNodeId(parentNode.id, {
              matching_condition: conditionData.matching_condition,
              match_type: conditionData.match_type,
            });
          }
        }
      }
    } else if (isTriggerNode(node)) {
      // node.data is typed as Trigger after type guard
      const triggerData = node.data;
      const matchingTrigger = triggers.find(
        trigger =>
          trigger.type === triggerData.type &&
          JSON.stringify(trigger) === JSON.stringify(triggerData)
      );
      if (matchingTrigger) {
        nodeId = matchingTrigger.id;
      }
    }

    return {
      id: nodeId,
      position: node.position,
      measured: node.measured,
    };
  });

  // {id, source, target, sourceHandle, targetHandle} just the keys
  const metadataEdges = edges.map(edge => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle,
    targetHandle: edge.targetHandle,
  }));

  const metadata = {
    nodes: metadataNodes,
    edges: metadataEdges,
  };

  // Create agent data without nodes and edges, but with metadata
  const {
    nodes: _unusedNodes,
    edges: _unusedEdges,
    ...baseWorkflowAgentWithoutGraph
  } = baseWorkflowAgent;

  const agentData = {
    ...baseWorkflowAgentWithoutGraph,
    steps,
    start_step_id: startStepId,
    agent_metadata: metadata,
  };

  return { agentData, triggers };
}

/**
 * Process a step node and return the corresponding Step.
 * Dispatches to the appropriate processor based on node type.
 */
function processStepNode(
  node: WorkflowNode,
  allNodes: WorkflowNode[],
  allEdges: WorkflowEdge[]
): Step | null {
  if (isForLoopNode(node)) {
    return processForLoopNode(node, allNodes, allEdges);
  } else if (isConditionalStepNode(node)) {
    return processConditionalNode(node, allNodes, allEdges);
  } else if (isSpecializedNode(node)) {
    return processAgentStep(node);
  }
  return null;
}

/**
 * Recursively process a ForLoop node and all its children.
 * The node is already typed as ForLoopNode, so node.data is ForLoopStep.
 */
function processForLoopNode(
  forLoopNode: ForLoopNode,
  allNodes: WorkflowNode[],
  allEdges: WorkflowEdge[]
): ForLoopStep {
  // node.data is already typed as ForLoopStep due to ForLoopNode type
  const forLoopData = forLoopNode.data;

  // Find all direct children of this ForLoop
  const childNodes = allNodes.filter(node => node.parentId === forLoopNode.id);

  const forLoopSteps: Record<string, Step> = {};

  // Process each child node recursively
  childNodes.forEach(childNode => {
    const step = processStepNode(childNode, allNodes, allEdges);
    if (step) {
      forLoopSteps[childNode.id] = step;
    }
  });

  // Set internal step relationships for this ForLoop
  const childNodeIds = new Set(childNodes.map(n => n.id));
  setNextStepRelationships(forLoopSteps, allEdges, childNodes, childNodeIds);

  return {
    step_id: forLoopNode.id,
    type: StepType.FOR_LOOP,
    name: forLoopData.name || 'For Loop',
    description: forLoopData.description || '',
    items_source: forLoopData.items_source || '$WORKFLOW_INPUT.items',
    item_name: forLoopData.item_name || 'item',
    max_iterations: forLoopData.max_iterations || null,
    parallel: forLoopData.parallel || false,
    steps: forLoopSteps,
    next_step_id: null, // Will be set by parent processing
    parameters: forLoopData.parameters || {},
    timeout_seconds: forLoopData.timeout_seconds || 3600,
  };
}

/**
 * Process a conditional node and consolidate connected condition nodes.
 * The node is already typed as ConditionalStepNode, so node.data is ConditionalStep.
 */
function processConditionalNode(
  conditionalNode: ConditionalStepNode,
  allNodes: WorkflowNode[],
  allEdges: WorkflowEdge[]
): ConditionalStep {
  // node.data is already typed as ConditionalStep due to ConditionalStepNode type
  const conditionalData = conditionalNode.data;

  // Find all condition nodes connected to this conditional node
  const conditionNodes = findConnectedConditionNodes(conditionalNode, allNodes, allEdges);

  const conditions: Condition[] = [];
  conditionNodes.forEach(conditionNode => {
    // conditionNode.data is already typed as ConditionNodeData due to ConditionBranchNode type
    const conditionData = conditionNode.data;
    // Find where this condition node leads to
    const outgoingEdge = allEdges.find(edge => edge.source === conditionNode.id);
    if (outgoingEdge) {
      conditions.push({
        matching_condition: conditionData.matching_condition || '',
        match_type: conditionData.match_type || MatchType.EQUALS,
        go_to_step_id: outgoingEdge.target,
      });
    }
  });

  return {
    step_id: conditionalNode.id,
    type: StepType.CONDITIONAL,
    name: conditionalData.name || 'Conditional',
    description: conditionalData.description || '',
    evaluator_agent_id: conditionalData.evaluator_agent_id || conditionalNode.id,
    conditions: conditions.length > 0 ? conditions : conditionalData.conditions || [],
    next_step_id: null, // Will be set by edge processing
    parameters: conditionalData.parameters || {},
    timeout_seconds: conditionalData.timeout_seconds || 3600,
  };
}

/**
 * Process a simple agent step.
 * The node is already typed as SpecializedNode, so node.data is SpecializedAgentStep.
 */
function processAgentStep(agentNode: SpecializedNode): SpecializedAgentStep {
  // node.data is already typed as SpecializedAgentStep due to SpecializedNode type
  const agentData = agentNode.data;

  return {
    step_id: agentNode.id,
    type: StepType.SPECIALIZED,
    name: agentData.name || 'Agent',
    description: agentData.description || '',
    agent_id: agentData.agent_id || agentNode.id,
    next_step_id: null, // Will be set by edge processing
    parameters: agentData.parameters || {},
    timeout_seconds: agentData.timeout_seconds || 3600,
  };
}

/**
 * Set next_step_id relationships based on edges.
 * If scopeNodeIds is provided, only processes edges between nodes in that set (for containers like ForLoop).
 * Otherwise processes edges between top-level nodes (no parentId).
 */
function setNextStepRelationships(
  steps: Record<string, Step>,
  edges: WorkflowEdge[],
  nodes: WorkflowNode[],
  scopeNodeIds?: Set<string>
): void {
  edges.forEach(edge => {
    const sourceNode = nodes.find(n => n.id === edge.source);
    const targetNode = nodes.find(n => n.id === edge.target);

    // Skip if source or target is missing, or if target is a condition node
    if (!sourceNode || !targetNode || targetNode.type === 'condition') {
      return;
    }

    // Skip if step doesn't exist in our steps record
    if (!steps[edge.source]) {
      return;
    }

    if (scopeNodeIds) {
      // Container mode: only process edges between nodes in the scope
      if (scopeNodeIds.has(edge.source) && scopeNodeIds.has(edge.target)) {
        steps[edge.source].next_step_id = edge.target;
      }
    } else {
      // Top-level mode: only process edges between nodes without parentId
      if (!sourceNode.parentId && !targetNode.parentId) {
        steps[edge.source].next_step_id = edge.target;
      }
    }
  });
}

/**
 * Find start step (node with no incoming edges)
 */
function findStartStep(nodes: WorkflowNode[], edges: WorkflowEdge[]): string {
  const nodeIds = new Set(nodes.map(node => node.id));

  for (const node of nodes) {
    if (isStepNode(node)) {
      // Check if this node has incoming edges from other nodes at the same level
      const hasIncomingEdge = edges.some(
        edge => edge.target === node.id && nodeIds.has(edge.source)
      );

      if (!hasIncomingEdge) {
        return node.id;
      }
    }
  }

  // Fallback to first agent node
  const firstAgentNode = nodes.find(node => isStepNode(node));
  return firstAgentNode?.id || '';
}

/**
 * Find condition nodes connected to a conditional step.
 * Returns ConditionBranchNode[] - typed condition nodes.
 */
function findConnectedConditionNodes(
  conditionalNode: ConditionalStepNode,
  allNodes: WorkflowNode[],
  allEdges: WorkflowEdge[]
): ConditionBranchNode[] {
  const conditionNodes: ConditionBranchNode[] = [];

  // Find all outgoing edges from the conditional node
  const outgoingEdges = allEdges.filter(edge => edge.source === conditionalNode.id);

  outgoingEdges.forEach(edge => {
    const targetNode = allNodes.find(node => node.id === edge.target);
    if (targetNode && isConditionNode(targetNode)) {
      conditionNodes.push(targetNode);
    }
  });

  return conditionNodes;
}
